import { FormEvent, ReactNode, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import { emailProblem } from './emailValidation';

interface DialogProps {
  open: boolean;
  onClose(): void;
  update(value: string): unknown;
  title: string;
  description?: ReactNode;
  submitLabel?: string;
}

export function SetEmailDialog({ open, title, description, submitLabel = 'Lagre', onClose, update }: DialogProps) {
  const [value, setValue] = useState('');
  // Only complain once the field has been left or a save attempted, then keep the message live while typing.
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const email = value.trim();
  const problem = emailProblem(email);
  const showProblem = touched && !!problem;

  const handleClose = () => {
    setValue('');
    setTouched(false);
    setSaveError(null);
    onClose();
  };

  const commit = async (ev: FormEvent) => {
    ev.preventDefault();
    setTouched(true);
    if (problem) return;
    setSaving(true);
    setSaveError(null);
    try {
      await update(email);
      handleClose();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="xs">
      <Box component="form" noValidate onSubmit={commit}>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {description && <DialogContentText sx={{ mb: 1 }}>{description}</DialogContentText>}
          <TextField
            label="E-post"
            fullWidth
            autoFocus
            type="email"
            margin="normal"
            value={value}
            onChange={(ev) => {
              setValue(ev.target.value);
              setSaveError(null);
            }}
            onBlur={() => email && setTouched(true)}
            error={showProblem || !!saveError}
            helperText={showProblem ? problem : saveError ? `Kunne ikke lagre: ${saveError}` : ' '}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose}>Avbryt</Button>
          <Button type="submit" variant="contained" color="primary" loading={saving} disabled={!email || showProblem}>
            {submitLabel}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
